import '@eeacms/volto-tabs-block/less/menu.less';

import React from 'react';
import EditBlockWrapper from '@eeacms/volto-tabs-block/components/EditBlockWrapper';
import { defaultSchemaEnhancer } from '@eeacms/volto-tabs-block/components/templates/default/schema';
import { BlocksForm } from '@plone/volto/components/manage/Form';
import { emptyBlocksForm } from '@plone/volto/helpers/Blocks/Blocks';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { useIntl } from 'react-intl';
import { Input } from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';

import messages from '../../../../../../messages/tabs';

// Recebe apenas valores primitivos e callbacks estáveis: assim a navegação não
// re-renderiza enquanto se digita no conteúdo da aba.
export const MenuItem = React.memo(function MenuItem({
  tab,
  index,
  title,
  isActive = false,
  isEditing = false,
  isLast = false,
  selected = false,
  onSelect = noop,
  onStartEditing = noop,
  onRename = noop,
  onAddTab = noop,
}) {
  const inputRef = React.useRef(null);
  const intl = useIntl();
  const defaultTitle = intl.formatMessage(messages.TabNum, {
    index: index + 1,
  });
  const tabTitle = title || defaultTitle;

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  return (
    <>
      <li className={cx('tab-item', { active: isActive })}>
        {isEditing && selected ? (
          <Input
            placeholder={defaultTitle}
            ref={inputRef}
            transparent
            value={title ?? ''}
            onChange={(e) => {
              onRename(tab, e.target.value);
            }}
          />
        ) : (
          <button
            type="button"
            name={defaultTitle}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.target.tagName === 'A' && e.code === 'Space') {
                e.preventDefault();
                onSelect(tab);
              }
            }}
            onClick={() => {
              onSelect(tab);
            }}
            onDoubleClick={() => {
              onStartEditing(tab);
            }}
          >
            <span className="name">{tabTitle}</span>
          </button>
        )}
      </li>

      {isLast ? (
        <li className="tab-item">
          <button
            type="button"
            name="addition"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.code === 'Space') {
                e.preventDefault();
                onAddTab();
              }
            }}
            onClick={() => {
              onAddTab();
            }}
          >
            <i className="fa-solid fa-plus" />
          </button>
        </li>
      ) : null}
    </>
  );
});

const Edit = (props) => {
  const intl = useIntl();
  const {
    activeBlock = null,
    activeTab = null,
    activeTabIndex = 0,
    block = null,
    data = {},
    selected = false,
    editingTab = null,
    manage = false,
    metadata = null,
    multiSelected = [],
    tabs = {},
    tabsData = {},
    tabsList = [],
    emptyTab = () => {},
    onChangeBlock = noop,
    onChangeTabData = noop,
    onSelectBlock = noop,
    setEditingTab = noop,
    schema,
  } = props;
  // Os handlers leem sempre o estado mais recente por ref, para poderem ser
  // estáveis e não invalidarem a memoização da navegação.
  const latest = React.useRef(null);
  latest.current = {
    activeBlock,
    activeTab,
    block,
    data,
    editingTab,
    emptyTab,
    intl,
    schema,
    tabs,
    tabsData,
    onChangeBlock,
    setActiveBlock: props.setActiveBlock,
    setActiveTab: props.setActiveTab,
    setEditingTab,
  };

  const handleSelectTab = React.useCallback((tab) => {
    const current = latest.current;
    if (current.activeTab !== tab) {
      current.setActiveTab?.(tab);
    }
    if (current.activeBlock) {
      current.setActiveBlock?.(null);
    }
    if (current.editingTab !== tab) {
      current.setEditingTab?.(null);
    }
  }, []);

  const handleStartEditing = React.useCallback((tab) => {
    latest.current.setEditingTab?.(tab);
  }, []);

  const handleRenameTab = React.useCallback((tab, value) => {
    const current = latest.current;
    current.onChangeBlock(current.block, {
      ...current.data,
      data: {
        ...current.tabsData,
        blocks: {
          ...current.tabsData.blocks,
          [tab]: {
            ...(current.tabs[tab] || {}),
            title: value,
          },
        },
      },
    });
  }, []);

  const handleAddTab = React.useCallback(() => {
    const current = latest.current;
    const tabId = uuid();

    current.onChangeBlock(current.block, {
      ...current.data,
      data: {
        ...current.tabsData,
        blocks: {
          ...current.tabsData.blocks,
          [tabId]: {
            ...current.emptyTab({
              schema: current.schema?.properties?.data?.schema || {},
              intl: current.intl,
            }),
          },
        },
        blocks_layout: {
          items: [...current.tabsData.blocks_layout.items, tabId],
        },
      },
    });

    current.setActiveTab?.(tabId);
    current.setEditingTab?.(null);
  }, []);

  const titlesKey = tabsList
    .map((tab) => `${tab}:${tabs[tab]?.title ?? ''}`)
    .join('|');

  const menuItems = React.useMemo(
    () =>
      tabsList.map((tab, index) => (
        <MenuItem
          key={tab}
          tab={tab}
          index={index}
          title={tabs[tab]?.title}
          isActive={tab === activeTab}
          isEditing={editingTab === tab}
          isLast={index === tabsList.length - 1}
          selected={selected}
          onSelect={handleSelectTab}
          onStartEditing={handleStartEditing}
          onRename={handleRenameTab}
          onAddTab={handleAddTab}
        />
      )),
    // `titlesKey` cobre mudanças em `tabsList`/`tabs` sem depender da
    // identidade desses objetos, que muda a cada tecla digitada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      titlesKey,
      activeTab,
      editingTab,
      selected,
      handleSelectTab,
      handleStartEditing,
      handleRenameTab,
      handleAddTab,
    ],
  );

  const currentTab = tabsList[activeTabIndex];

  return (
    <div className="br-tab" style={{ marginTop: 14 }}>
      <nav className="tab-nav">
        <ul>{menuItems}</ul>
      </nav>
      {activeTabIndex >= 0 && currentTab ? (
        <div className="tab-content">
          <div
            key={currentTab}
            className={cx('tab-panel', 'active')}
            id={activeTab}
          >
            <BlocksForm
              errors={props.errors}
              blocksErrors={props.blocksErrors}
              allowedBlocks={data?.allowedBlocks}
              description={data?.instructions?.data}
              manage={manage}
              metadata={metadata}
              pathname={props.pathname}
              properties={
                isEmpty(tabs[currentTab]) ? emptyBlocksForm() : tabs[currentTab]
              }
              // Sem isso o formulário da aba também monta a árvore do painel
              // "Ordem" da barra lateral, duplicando o trabalho a cada tecla.
              isMainForm={false}
              selected={selected && activeBlock}
              selectedBlock={selected && activeBlock ? activeBlock : null}
              title={data?.placeholder}
              onChangeField={onChangeTabData}
              onChangeFormData={(newFormData) => {
                onChangeBlock(block, {
                  ...data,
                  data: {
                    ...tabsData,
                    blocks: {
                      ...tabsData.blocks,
                      [activeTab]: {
                        ...(newFormData.blocks_layout.items.length > 0
                          ? newFormData
                          : emptyTab({
                              schema: schema?.properties?.data?.schema || {},
                              intl,
                            })),
                      },
                    },
                  },
                });
              }}
              onSelectBlock={(id, selected, e) => {
                const isMultipleSelection = e
                  ? e.shiftKey || e.ctrlKey || e.metaKey
                  : false;
                onSelectBlock(
                  id,
                  activeBlock === id ? false : isMultipleSelection,
                  e,
                );
                setEditingTab(null);
              }}
            >
              {({ draginfo }, editBlock, blockProps) => {
                return (
                  <EditBlockWrapper
                    blockProps={blockProps}
                    draginfo={draginfo}
                    multiSelected={multiSelected.includes(blockProps.block)}
                  >
                    {editBlock}
                  </EditBlockWrapper>
                );
              }}
            </BlocksForm>
          </div>
        </div>
      ) : null}
    </div>
  );
};

Edit.schemaEnhancer = defaultSchemaEnhancer;

export default Edit;
